import {
        FaDatabase,
        FaLayerGroup,
        FaProjectDiagram,
        FaCodeBranch,
        FaCalendarAlt,
        FaListOl,
        FaCode,
        FaNetworkWired,
        FaChartLine,
        FaTrophy
} from 'react-icons/fa';

export const sqlIntermediateMetadata = {
        id: 'sql-intermediate',
        title: "SQL Intermediate",
        company: "StreamPlus Analytics",
        category: "Applied Analytics",
        cardDescription: "Level up your SQL skills. Master multi-table joins, subqueries, complex logic, and analytics using a realistic Streaming TV dataset.",
        learningObjectives: [
                "Complex Data Relationships (Multi-JOINs, LEFT vs INNER)",
                "Advanced Logic (CASE, Subqueries)",
                "Analytic Functions (Window Functions, Partitioning)",
                "Data Architecture (CTEs, UNION)",
                "Time-Series Analysis"
        ],
        scenario: (
                <div className="space-y-8">
                        {/* Hero Section - Clean, Minimalist, Website Theme */}
                        <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-orange-500/20 transition-all duration-500">
                                <div className="relative z-10">
                                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                                                StreamPlus <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Analytics</span>
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
                                                As the newly appointed <strong className="text-gray-900 dark:text-white font-semibold">Lead Data Analyst</strong>,
                                                you are tasked with uncovering deep insights into subscriber behavior.
                                                The board requires precise data on engagement, churn, and content performance.
                                        </p>
                                </div>
                                {/* Subtle Brand Accent */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
                        </div>

                        {/* Schema Grid - Strictly Professional, No Emojis */}
                        <div>
                                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
                                        Data Architecture
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        {[
                                                { table: 'users', desc: 'Demographics & Region' },
                                                { table: 'content', desc: 'Catalog Metadata' },
                                                { table: 'subscriptions', desc: 'Plans & Status' },
                                                { table: 'viewing_sessions', desc: 'Behavioral Logs' }
                                        ].map((item) => (
                                                <div key={item.table} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                                                        <div className="font-mono font-bold text-orange-600 dark:text-orange-400 text-sm mb-1">
                                                                {item.table}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                {item.desc}
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                        </div>
                </div>
        ),
        introduction: ``,
        hasSQL: true
};

export const sqlIntermediateChallenges = [
        {
                id: 'multi-join',
                title: 'Multi-Table Joins',
                question: `1. Start with \`viewing_sessions\` as your base table.
2. Join to \`users\` to get the \`country_code\`.
3. Join to \`content\` to get the \`genre\`.
4. Group by \`country_code\` and \`genre\`.
5. Sum the total \`watch_minutes\` for each combination.`,
                keyConcepts: ['INNER JOIN', 'Multiple Keys', 'Extended Schema'],
                defaultQuery: `-- TIP: Start from viewing_sessions, then JOIN outward
-- SELECT ...
-- FROM viewing_sessions
-- JOIN users ON viewing_sessions.user_id = users.user_id
-- JOIN content ON viewing_sessions.content_id = content.content_id
-- GROUP BY ...`,
                expectedColumns: [
                        { name: 'country_code', type: 'TEXT' },
                        { name: 'genre', type: 'TEXT' },
                        { name: 'total_watch_minutes', type: 'INTEGER' },
                ],
                businessRequest: `The Content Strategy Team needs to understand what genres are popular in which countries. They want a breakdown of total watch time by country and genre to optimise regional content licensing.`,
                concept: `**Multi-Table Joins**

Chain \`JOIN\` clauses to connect data across more than two tables. Start from your central fact table and join outward to the dimension tables holding the attributes you need.

\`\`\`sql
SELECT a.col, b.col, c.col
FROM table_a a
JOIN table_b b ON a.id = b.a_id
JOIN table_c c ON a.other_id = c.id
GROUP BY a.col, b.col
\`\`\`

- \`viewing_sessions\` holds both \`user_id\` and \`content_id\` - making it the natural central hub.
- Each \`JOIN\` adds one more table linked by a shared key column.
- Alias tables when joining 3+ to keep column references clear.

*Most databases optimise join order automatically - write for readability, not execution.*`,
                context: `**The Business Request**

The Content Strategy Team needs to understand **what genres** are popular in **which countries**. They want a breakdown of total watch time by country and genre to optimize regional content licensing.

**The Concept**

Multi-Table Joins allow you to connect data across many tables in a single query.
By chaining \`JOIN\` clauses, you can traverse the entire database schema to combine related information.

**The Syntax**

\`\`\`sql
SELECT columns
FROM table1
JOIN table2 ON table1.id = table2.t1_id
JOIN table3 ON table1.other_id = table3.id
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Start with \`viewing_sessions\` as your base table.
2. Join to \`users\` to get the \`country_code\`.
3. Join to \`content\` to get the \`genre\`.
4. Group by \`country_code\` and \`genre\`.
5. Sum the total \`watch_minutes\` for each combination.

</div>`,
                hint: `**Strategy:** Think of this as building a bridge between three islands of data.

**Step 1:** Start from \`viewing_sessions\`-your central hub containing both \`user_id\` and \`content_id\`.

**Step 2:** Connect to \`users\` using \`user_id\` to unlock \`country_code\`.

**Step 3:** Chain a second JOIN to \`content\` using \`content_id\` to access \`genre\`.

**Step 4:** Use \`GROUP BY\` on country and genre, then \`SUM()\` the watch time.`,
                solution: `SELECT users.country_code, content.genre, SUM(viewing_sessions.watch_minutes)
FROM viewing_sessions
JOIN users ON viewing_sessions.user_id = users.user_id
JOIN content ON viewing_sessions.content_id = content.content_id
GROUP BY users.country_code, content.genre`,
                explanation: `**Schema Navigation Mastered**

**Business Context:**

This query answers a common stakeholder question: "What content resonates with users from different countries?" Understanding regional preferences helps:
- **Content Acquisition**: Should we license more K-dramas for Asia?
- **Localization Priority**: Which genres need subtitles first?
- **Marketing Campaigns**: Target Action fans in the US vs. Romance fans in Europe.

**How Multi-Joins Work:**

Each \`JOIN\` clause adds another table to your query. The database engine finds matching rows based on your \`ON\` condition. Order matters for readability, but most databases optimize the execution order automatically.

**Pro Tip:** Always start from your "fact table" (viewing_sessions) and join outward to dimension tables (users, content). This "star schema" pattern is the foundation of data warehousing.`,
                concepts: [
                        {
                                icon: <FaNetworkWired className="text-3xl text-blue-600" />,
                                title: 'Multi-Join',
                                description: 'Chaining multiple JOIN clauses to connect disparate parts of a database schema.'
                        }
                ]
        },
        {
                id: 'table-aliases',
                title: 'Table Aliases',
                question: `Rewrite the multi-join query from Q1 using table aliases:
- \`viewing_sessions\` as \`vs\`
- \`users\` as \`u\`
- \`content\` as \`c\`

Return \`country_code\`, \`genre\`, and total \`watch_minutes\`.`,
                keyConcepts: ['Table Aliases', 'AS keyword', 'Readability'],
                defaultQuery: `-- TIP: Assign a short alias after each table name in FROM / JOIN
-- FROM viewing_sessions vs
-- JOIN users u ON vs.user_id = u.user_id
-- JOIN content c ON vs.content_id = c.content_id`,
                expectedColumns: [
                        { name: 'country_code', type: 'TEXT' },
                        { name: 'genre', type: 'TEXT' },
                        { name: 'total_minutes', type: 'INTEGER' },
                ],
                businessRequest: `The previous query works but is verbose. In production, tables have names like \`user_subscription_history\` - without aliases, complex queries become unreadable walls of text. Rewrite using short aliases.`,
                concept: `**Table Aliases**

A table alias assigns a short nickname to a table for the duration of the query. Once set in \`FROM\` or \`JOIN\`, use the alias everywhere instead of the full table name.

\`\`\`sql
SELECT u.column, vs.column
FROM table_name u
JOIN other_table vs ON u.key = vs.key
\`\`\`

- Assign the alias directly after the table name in \`FROM\` or \`JOIN\`.
- The \`AS\` keyword is optional: \`FROM users u\` and \`FROM users AS u\` are identical.
- Convention: lowercase initials (\`u\` for users) or abbreviations (\`vs\` for viewing_sessions).

*Aliases become essential with 4+ table joins - they are standard in all professional SQL.*

**Pro Tip:** The \`AS\` keyword is optional - \`FROM users u\` and \`FROM users AS u\` are identical. Once you assign an alias, use it consistently throughout the query. Mixing the alias and the full table name in the same query will cause an error.`,
                context: `**The Business Request**

The previous multi-join query worked, but it was verbose. When you have long table names or join 4+ tables, repeating full table names makes queries hard to read.

**The Concept**

A **Table Alias** assigns a short name (like \`vs\`, \`u\`, \`c\`) to a table for use in the rest of the query.
This is especially valuable when joining multiple tables.

**The Syntax**

\`\`\`sql
SELECT alias.column, ...
FROM table_name alias
JOIN other_table alias2 ON alias.key = alias2.key
GROUP BY ...
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Rewrite the multi-join query from Q1 using table aliases:
- \`viewing_sessions\` -> \`vs\`
- \`users\` -> \`u\`
- \`content\` -> \`c\`

Return \`country_code\`, \`genre\`, and total \`watch_minutes\`.

</div>`,
                hint: `After each table name in the FROM/JOIN clause, add a space and the alias.
Then use the alias (e.g., \`u.country_code\`) instead of the full table name everywhere.`,
                solution: `SELECT u.country_code, c.genre, SUM(vs.watch_minutes) as total_minutes
FROM viewing_sessions vs
JOIN users u ON vs.user_id = u.user_id
JOIN content c ON vs.content_id = c.content_id
GROUP BY u.country_code, c.genre`,
                explanation: `**Professional SQL Style**

**Business Context:**

In production environments, analysts work with tables like \`user_subscription_history\`, \`content_viewing_sessions\`, and \`marketing_campaign_attribution\`. Without aliases, queries become unmanageable walls of text.

**How Aliases Work:**

Aliases are assigned in the \`FROM\`/\`JOIN\` clause and apply to the entire query. The \`AS\` keyword is optional in most databases: \`FROM users AS u\` and \`FROM users u\` are equivalent.

**When to Use Aliases:**
- Always when joining 2+ tables
- Essential for **self-joins** (joining a table to itself)
- Required when using the same table twice in one query

**Naming Convention:** Use lowercase initials (\`u\` for users) or meaningful abbreviations (\`vs\` for viewing_sessions).`,
                concepts: [
                        {
                                icon: <FaCode className="text-3xl text-purple-500" />,
                                title: 'Table Aliases',
                                description: 'Short names for tables that improve query readability and reduce typing in complex joins.'
                        }
                ]
        },
        {
                id: 'left-vs-inner',
                title: 'LEFT JOIN vs INNER JOIN',
                question: `1. \`LEFT JOIN\` \`users\` to \`viewing_sessions\` on \`user_id\`.
2. Filter for rows where \`viewing_sessions.session_id\` IS NULL.
3. Return the \`user_id\`.`,
                keyConcepts: ['LEFT JOIN', 'INNER JOIN', 'NULL matches'],
                defaultQuery: `-- TIP: LEFT JOIN keeps ALL rows from the left table
-- WHERE right_table.key IS NULL  <-- finds rows with no match
-- SELECT ...
-- FROM users
-- LEFT JOIN viewing_sessions ON ...
-- WHERE viewing_sessions.session_id IS NULL`,
                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                ],
                businessRequest: `The Marketing Team needs to identify inactive users for a re-engagement campaign. They want a list of users who signed up but have never watched anything.`,
                concept: `**LEFT JOIN vs INNER JOIN**

An \`INNER JOIN\` returns only rows where a match exists in both tables. A \`LEFT JOIN\` keeps every row from the left table, filling in \`NULL\` for right-side columns when no match is found.

\`\`\`sql
SELECT a.id
FROM table_a
LEFT JOIN table_b ON table_a.id = table_b.a_id
WHERE table_b.id IS NULL
\`\`\`

- \`INNER JOIN\` - matched rows only.
- \`LEFT JOIN\` - all left rows, with \`NULL\` on the right when no match exists.
- Filtering for \`WHERE right_table.key IS NULL\` isolates rows with no related data.

*This anti-join pattern is one of the most useful in analytics: find customers with no orders, users with no sessions, accounts with no payments.*

**Pro Tip:** If your \`LEFT JOIN\` produces unexpected \`NULL\` values, temporarily remove the \`WHERE\` clause to see the full join output. That lets you verify the join condition is correct before adding the filter back.`,
                context: `** The Business Request **

        The Marketing Team needs to identify inactive users for a ** re - engagement campaign **.We need to find users who have signed up but have ** never ** watched anything.

** The Concept **

        An \`INNER JOIN\` only shows matches. If we want to find users who have **NO** watch history, an Inner Join would hide them.
A \`LEFT JOIN\` keeps every user, filling in \`NULL\` for their session data if they haven't watched anything.

**The Syntax**

\`\`\`sql
SELECT *
FROM table_A
LEFT JOIN table_B ON table_A.id = table_B.a_id
WHERE table_B.id IS NULL
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. \`LEFT JOIN\` \`users\` to \`viewing_sessions\` on \`user_id\`.
2. Filter for rows where \`session_id\` IS NULL.
3. Return the \`user_id\`.

</div>`,
                hint: `Use a \`LEFT JOIN\` to ensure you keep all users, even those without sessions.
Then, look for rows where the \`viewing_sessions.session_id\` is NULL.`,
                solution: `SELECT users.user_id
FROM users
LEFT JOIN viewing_sessions ON users.user_id = viewing_sessions.user_id
WHERE viewing_sessions.session_id IS NULL`,
                explanation: `**Finding the Invisible**

**Business Context:**

Identifying inactive users is critical for:
- **Re-engagement Campaigns**: Send "We miss you!" emails to dormant accounts
- **Churn Prevention**: Understand why users signed up but never engaged
- **Onboarding Analysis**: Is there friction after signup?

**How LEFT JOIN Works:**

\`LEFT JOIN\` preserves ALL rows from the left table, regardless of matches. When no match exists in the right table, those columns return \`NULL\`.

| Join Type | Behavior |
|-----------|----------|
| INNER JOIN | Only matching rows from both tables |
| LEFT JOIN | All left rows + matching right (NULL if no match) |
| RIGHT JOIN | All right rows + matching left (NULL if no match) |
| FULL OUTER JOIN | All rows from both tables |

**Pattern for "Find Missing":** Always use \`LEFT JOIN\` + \`WHERE right_table.key IS NULL\`.`,
                concepts: [
                        {
                                icon: <FaCodeBranch className="text-3xl text-orange-500" />,
                                title: 'LEFT JOIN',
                                description: 'Retains all records from the left table, inserting NULLs where no match exists in the right table.'
                        }
                ]
        },
        {
                id: 'string-operations',
                title: 'String Operations',
                question: `1. Find all content where the title contains "action" (case-insensitive).
2. Return \`title\`, \`genre\` in uppercase, and a \`display_name\` that combines both.`,
                keyConcepts: ['LIKE', 'LOWER', 'UPPER', 'CONCAT', 'Pattern Matching'],
                defaultQuery: `-- TIP: Wrap column in LOWER() for case-insensitive LIKE
-- LOWER(title) LIKE '%pattern%'
-- UPPER(column)  ->  converts to uppercase
-- CONCAT(col1, ' - ', col2)  ->  joins strings`,
                expectedColumns: [
                        { name: 'title', type: 'TEXT' },
                        { name: 'genre_upper', type: 'TEXT' },
                        { name: 'display_name', type: 'TEXT' },
                ],
                businessRequest: `The Content Team wants all titles containing "Action" found via case-insensitive search, and a standardized display name combining title and genre for use in the UI.`,
                concept: `**String Functions**

SQL provides text manipulation functions for searching, transforming, and combining string values.

\`\`\`sql
SELECT UPPER(column) AS upper_col,
       CONCAT(col1, ' - ', col2) AS combined
FROM table
WHERE LOWER(column) LIKE '%pattern%'
\`\`\`

- \`LIKE '%pattern%'\` matches the pattern anywhere in the string (\`%\` is a wildcard).
- Wrap the column in \`LOWER()\` before \`LIKE\` for case-insensitive matching.
- \`CONCAT()\` joins any number of strings; \`||\` is an equivalent alternative.

*\`LIKE 'A%'\` matches strings starting with A. \`LIKE '%A'\` matches strings ending with A.*

**Pro Tip:** \`LIKE\` is case-sensitive by default in most databases. Always wrap the column in \`LOWER()\` and use a lowercase pattern - \`LOWER(title) LIKE '%action%'\` - to guarantee case-insensitive matching regardless of the database.`,
                context: `**The Business Request**

The Content Team wants to find all movies/shows with "Action" in the title (case-insensitive search), and create a standardized display name combining title and genre.

**The Concept**

SQL provides powerful string manipulation functions:
- \`LIKE\` with wildcards (\`%\`) for pattern matching
- \`LOWER()\` / \`UPPER()\` for case-insensitive comparisons
- \`CONCAT()\` or \`||\` operator to combine strings

**The Syntax**

\`\`\`sql
SELECT column, UPPER(...) AS ..., 
       CONCAT(...) AS display_name
FROM table
WHERE LOWER(column) LIKE '%pattern%'
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Find all content where the title contains "action" (case-insensitive).
2. Return \`title\`, \`genre\` in uppercase, and a \`display_name\` that combines title and genre.

</div>`,
                hint: `Use \`LOWER(title) LIKE '%action%'\` for case-insensitive matching.
Use \`CONCAT(title, ' - ', UPPER(genre))\` or the \`||\` operator to build the display name.`,
                solution: `SELECT 
  title,
  UPPER(genre) AS genre_upper,
  CONCAT(title, ' - ', UPPER(genre)) AS display_name
FROM content
WHERE LOWER(title) LIKE '%action%'`,
                explanation: `**Text Manipulation Mastered**

**Business Context:**

String operations power real-world features like:
- **Search**: Case-insensitive title matching for user queries
- **Data Cleaning**: Standardizing inconsistent entries ("action", "Action", "ACTION")
- **Display Formatting**: Creating user-friendly labels from raw data

**How String Functions Work:**

| Function | Purpose | Example |
|----------|---------|--------|
| \`LIKE\` | Pattern matching | \`'%action%'\` matches anywhere |
| \`LOWER()\` | Convert to lowercase | \`LOWER('ABC')\` -> 'abc' |
| \`UPPER()\` | Convert to uppercase | \`UPPER('abc')\` -> 'ABC' |
| \`CONCAT()\` | Join strings | \`CONCAT(a, b)\` or \`a || b\` |

**LIKE Wildcards:**
- \`%\` matches zero or more characters
- \`_\` matches exactly one character
- \`LIKE 'A%'\` -> starts with A
- \`LIKE '%A'\` -> ends with A`,
                concepts: [
                        {
                                icon: <FaCode className="text-3xl text-blue-500" />,
                                title: 'String Functions',
                                description: 'LIKE for pattern matching, LOWER/UPPER for case conversion, and CONCAT for combining text.'
                        }
                ]
        },
        {
                id: 'subqueries',
                title: 'Subqueries - Logic inside Logic',
                question: `1. Calculate the global average \`watch_minutes\` (this is your subquery).
2. Find all sessions where \`watch_minutes\` is **greater than** that average.
3. Select \`session_id\` and \`watch_minutes\`.`,
                keyConcepts: ['Subquery', 'Scalar Subquery', 'Dynamic Filtering'],
                defaultQuery: `-- TIP: A subquery in WHERE runs first and returns one value
-- WHERE column > (SELECT AVG(column) FROM table)`,
                expectedColumns: [
                        { name: 'session_id', type: 'INTEGER' },
                        { name: 'watch_minutes', type: 'INTEGER' },
                ],
                businessRequest: `The Product Team wants to define "high engagement" dynamically - any session longer than the global average, with the threshold recalculating automatically as data grows.`,
                concept: `**Scalar Subqueries**

A subquery is a query nested inside the main query. A scalar subquery returns a single value that can be used in a \`WHERE\` condition, making your filter dynamic rather than hardcoded.

\`\`\`sql
SELECT column
FROM table
WHERE column > (SELECT AVG(column) FROM table)
\`\`\`

- The inner query executes first, returning one value.
- The outer query uses that value in its \`WHERE\` clause.
- Dynamic thresholds recalculate automatically as new data is added.

*When the subquery is in \`WHERE\`, it runs once. When in \`SELECT\`, it may run per row - watch for performance on large tables.*

**Pro Tip:** You can nest a subquery almost anywhere - in \`WHERE\`, \`FROM\`, or even \`SELECT\`. When used in \`FROM\`, the subquery acts as a temporary table (a "derived table"). CTEs (the \`WITH\` clause) are the cleaner, more readable alternative for complex cases.`,
                context: `**The Business Request**

The Product Team wants to define what "high engagement" looks like. They need to compare every single session against the **Global Average** to identify power users.

**The Concept**

A **Subquery** is a query nested inside another query.
It allows you to calculate a value dynamically and use it in your main query without hardcoding.

**The Syntax**

\`\`\`sql
SELECT column
FROM table
WHERE column > (SELECT AVG(column) FROM table)
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Calculate the global average \`watch_minutes\` (this is your subquery).
2. Find all sessions where \`watch_minutes\` is **greater than** that average.
3. Select \`session_id\` and \`watch_minutes\`.

</div>`,
                hint: `Write a subquery to get \`AVG(watch_minutes)\`. Use that subquery in your \`WHERE\` clause: \`watch_minutes > (SELECT ...)\`.`,
                solution: `SELECT session_id, watch_minutes
FROM viewing_sessions
WHERE watch_minutes > (SELECT AVG(watch_minutes) FROM viewing_sessions)`,
                explanation: `**Dynamic Thresholds**

**Business Context:**

Subqueries enable self-adjusting analysis. Instead of hardcoding "sessions over 45 minutes," you compare against the actual average-which changes as your data grows.

**How Subqueries Work:**

1. The **inner query** executes first, returning a single value (scalar subquery)
2. The **outer query** uses that value in its \`WHERE\` clause
3. Each time the query runs, the threshold recalculates

**Types of Subqueries:**
- **Scalar**: Returns one value (used with =, >, <)
- **Row**: Returns one row with multiple columns
- **Table**: Returns multiple rows (used with \`IN\`, \`EXISTS\`)

**When to Use:**
- Comparing rows to aggregates (above/below average)
- Building dynamic thresholds from the data itself
- Filtering based on complex conditions

**Performance Note:** Subqueries in \`WHERE\` run once. Subqueries in \`SELECT\` may run per row-watch for slowdowns on large datasets.`,
                concepts: [
                        {
                                icon: <FaDatabase className="text-3xl text-purple-500" />,
                                title: 'Subquery',
                                description: 'A nested query used to calculate diverse values dynamically within the main query.'
                        }
                ]
        },
        {
                id: 'case-logic',
                title: 'CASE - Conditional Logic',
                question: `Create a new column called \`engagement_level\` for every session:
- \`watch_minutes > 60\` - 'High'
- \`watch_minutes > 20\` - 'Medium'
- Else - 'Low'

Select \`session_id\`, \`watch_minutes\`, and your new label.`,
                keyConcepts: ['CASE', 'WHEN', 'THEN', 'ELSE', 'Business Logic'],
                defaultQuery: `-- TIP: CASE is like IF/ELSE for SQL
-- CASE
--   WHEN condition THEN 'Label'
--   WHEN other_condition THEN 'Other'
--   ELSE 'Default'
-- END AS column_name`,
                expectedColumns: [
                        { name: 'session_id', type: 'INTEGER' },
                        { name: 'watch_minutes', type: 'INTEGER' },
                        { name: 'engagement_level', type: 'TEXT' },
                ],
                businessRequest: `Executives don't want to read raw minutes. The Product Team needs every session labelled as High, Medium, or Low engagement so reports can be summarised and filtered at a glance.`,
                concept: `**The CASE Statement**

\`CASE\` applies conditional logic row by row, generating a new column value based on matching rules. It is the SQL equivalent of IF/ELSE.

\`\`\`sql
CASE
  WHEN condition1 THEN 'Label A'
  WHEN condition2 THEN 'Label B'
  ELSE 'Label C'
END AS column_name
\`\`\`

- Conditions are evaluated top to bottom - the first match wins.
- Place the most specific condition first (e.g. \`> 60\` before \`> 20\`).
- \`ELSE\` is optional but strongly recommended - without it, unmatched rows return \`NULL\`.

*Use \`CASE\` in \`SELECT\` to create labels, in \`ORDER BY\` for custom sort order, and in \`GROUP BY\` to bucket rows on the fly.*`,
                context: `**The Business Request**

To simplify reporting for executives, we need to group sessions into readable categories rather than showing raw minutes.
We need to apply **Business Rules** to label every session.

**The Concept**

The \`CASE\` statement is like an If/Else switch.
It allows you to go row-by-row and generate a new custom value based on logic you define.

**The Syntax**

\`\`\`sql
CASE
  WHEN minutes < 10 THEN 'Short'
  WHEN minutes < 60 THEN 'Medium'
  ELSE 'Long'
END AS label_name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Create a new column called \`engagement_level\` for every session:
- \`watch_minutes > 60\` -> 'High'
- \`watch_minutes > 20\` -> 'Medium'
- Else -> 'Low'

Select \`session_id\`, \`watch_minutes\`, and your new label.

</div>`,
                hint: `Use the \`CASE WHEN ... THEN ... ELSE ... END\` syntax within your SELECT list.
Remember to give the CASE statement an alias using \`AS engagement_level\`.`,
                solution: `SELECT 
  session_id, 
  watch_minutes,
  CASE
    WHEN watch_minutes > 60 THEN 'High'
    WHEN watch_minutes > 20 THEN 'Medium'
    ELSE 'Low'
  END AS engagement_level
FROM viewing_sessions`,
                explanation: `**Business Categories Created**

**Business Context:**

Raw numbers rarely make it to executive dashboards. Stakeholders prefer categories:
- "High/Medium/Low engagement" is more actionable than "47.3 minutes"
- Enables segmenting users for targeted campaigns
- Simplifies communication: "60% of sessions are Low engagement"

**How CASE Works:**

\`CASE\` evaluates conditions top-to-bottom and returns the first match:
1. Checks \`WHEN\` conditions in order
2. Returns the matching \`THEN\` value
3. Falls through to \`ELSE\` if no conditions match
4. Returns \`NULL\` if no \`ELSE\` and no match

**Variations:**

\`\`\`sql
-- Simple CASE (equality check)
CASE status WHEN 'active' THEN 'Yes' ELSE 'No' END

-- Searched CASE (any condition)
CASE WHEN age >= 18 THEN 'Adult' ELSE 'Minor' END
\`\`\`

**Pro Tip:** Order your \`WHEN\` conditions from most specific to least specific. If you put \`> 20\` before \`> 60\`, the \`> 60\` case would never be reached.`,
                concepts: [
                        {
                                icon: <FaCode className="text-3xl text-teal-500" />,
                                title: 'CASE',
                                description: 'Applies conditional (If-Then-Else) logic to transform data row-by-row.'
                        }
                ]
        },
        {
                id: 'advanced-aggregations',
                title: 'Advanced Aggregations',
                question: `Create a summary table grouped by \`genre\` that shows:
1. Total Watch Time (\`SUM\`)
2. Average Session Length (\`AVG\`)
3. Longest Single Session (\`MAX\`)`,
                keyConcepts: ['Multiple Aggregates', 'ROUND', 'Formatting'],
                defaultQuery: `-- TIP: Combine multiple aggregates in one GROUP BY query
-- SUM(col), AVG(col), MAX(col)
-- Don't forget to JOIN content to get the genre column`,
                expectedColumns: [
                        { name: 'genre', type: 'TEXT' },
                        { name: 'total_minutes', type: 'INTEGER' },
                        { name: 'avg_minutes', type: 'DECIMAL' },
                        { name: 'max_session', type: 'INTEGER' },
                ],
                businessRequest: `The executive team needs a Genre Performance Report with multiple angles simultaneously - total volume, typical session length, and the longest recorded session - to understand how each genre is consumed.`,
                concept: `**Multiple Aggregations**

SQL can compute several aggregate metrics in a single query pass. Combine \`SUM\`, \`AVG\`, and \`MAX\` across the same \`GROUP BY\` to produce a multi-angle summary without running separate queries.

\`\`\`sql
SELECT category,
       SUM(col) AS total,
       AVG(col) AS average,
       MAX(col) AS peak
FROM table
GROUP BY category
\`\`\`

- \`SUM()\` - total of all values in the group.
- \`AVG()\` - mean value; use \`ROUND(AVG(col), 2)\` for cleaner decimal output.
- \`MAX()\` / \`MIN()\` - extreme values within the group.

*Running multiple aggregations in one query is more efficient than combining separate results in a spreadsheet.*`,
                context: `**The Business Request**

We need a **Genre Performance Report**.
Simple counts aren't enough. We need to look at data from multiple angles: Volume (Total), Intensity (Average), and Extremes (Max) to truly understand viewer habits.

**The Concept**

SQL allows you to calculate multiple metrics in a single pass.
You can \`SUM\`, \`AVG\`, and \`MAX\` different columns simultaneously while grouped by a category.

**The Syntax**

\`\`\`sql
SELECT category, SUM(col), AVG(col), MAX(col)
FROM table_name
GROUP BY category
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Create a summary table grouped by \`genre\` that shows:
1. Total Watch Time (\`SUM\`)
2. Average Session Length (\`AVG\`)
3. Longest Single Session (\`MAX\`)

</div>`,
                hint: `You need to join \`content\` and \`viewing_sessions\` first. Then group by \`genre\`.`,
                solution: `SELECT 
  content.genre,
  SUM(viewing_sessions.watch_minutes) as total_minutes,
  AVG(viewing_sessions.watch_minutes) as avg_minutes,
  MAX(viewing_sessions.watch_minutes) as max_session
FROM content
JOIN viewing_sessions ON content.content_id = viewing_sessions.content_id
GROUP BY content.genre`,
                explanation: `**Multi-Metric Analysis**

**Business Context:**

Executives need multiple perspectives simultaneously:
- **SUM**: Total resources consumed ("How much are we serving?")
- **AVG**: Typical behavior ("What's normal?")
- **MAX**: Extreme cases ("What's the ceiling?")
- **COUNT**: Volume ("How many?")

**How Aggregations Work:**

Aggregation functions collapse multiple rows into summary values:

| Function | Returns | Use Case |
|----------|---------|----------|
| \`SUM()\` | Total of all values | Revenue, watch time |
| \`AVG()\` | Mean value | Average order size |
| \`MAX()\`/\`MIN()\` | Extreme values | Longest session, lowest price |
| \`COUNT()\` | Number of rows | Total transactions |
| \`COUNT(DISTINCT)\` | Unique values | Unique customers |

**Pro Tip:** Combine with \`GROUP BY\` to get aggregates per category. The combination of multiple metrics reveals patterns like: "Documentaries have fewer sessions but longer average watch times."`,
                concepts: [
                        {
                                icon: <FaChartLine className="text-3xl text-green-500" />,
                                title: 'Metrics',
                                description: 'Computes multiple statistical measures (sum, avg, max) in a single pass.'
                        }
                ]
        },
        {
                id: 'window-functions',
                title: 'Window Functions - Ranking',
                question: `Rank every piece of content based on its \`duration_min\`.
Show \`title\`, \`duration_min\`, and its \`rank\` (Longest = Rank 1).`,
                keyConcepts: ['RANK()', 'OVER', 'Window Functions', 'Non-destructive'],
                defaultQuery: `-- TIP: RANK() adds a rank column without collapsing rows
-- RANK() OVER (ORDER BY column DESC) AS rank_col`,
                expectedColumns: [
                        { name: 'title', type: 'TEXT' },
                        { name: 'duration_min', type: 'INTEGER' },
                        { name: 'duration_rank', type: 'INTEGER' },
                ],
                businessRequest: `Marketing wants to feature the "Longest Content" titles on the homepage carousel. They need a numbered ranking - 1st, 2nd, 3rd longest - across the entire catalog.`,
                concept: `**Window Functions - RANK()**

Window functions calculate a value across a set of rows without collapsing them. \`RANK()\` assigns a position to each row based on an \`ORDER BY\` inside the \`OVER\` clause - all original rows remain in the output.

\`\`\`sql
RANK() OVER (ORDER BY column DESC) AS rank_col
\`\`\`

- Unlike \`GROUP BY\`, window functions add a column while keeping every row.
- \`ORDER BY\` inside \`OVER\` controls ranking direction; \`DESC\` puts the largest first.
- Ties receive the same rank; the next rank after a tie skips a number (1, 1, 3...).

*Use \`DENSE_RANK()\` if you don't want gaps after ties. Use \`ROW_NUMBER()\` when you need strictly unique positions.*`,
                context: `**The Business Request**

The Marketing Team wants to feature our "Longest Movies & Shows" on the homepage.
They need a **Ranked List** (1, 2, 3...) of all content based on duration.

**The Concept**

\`RANK()\` is a Window Function.
Unlike \`GROUP BY\`, which collapses rows, Window Functions keep every row but add a new attribute (like a Rank) based on its position in the sorted list.

**The Syntax**

\`\`\`sql
RANK() OVER (ORDER BY score_column DESC)
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Rank every piece of content based on its \`duration_min\`.
Show \`title\`, \`duration_min\`, and its \`rank\` (Longest = Rank 1).

</div>`,
                hint: `Use \`RANK() OVER (...)\` in your select list.
Inside the OVER clause, specify \`ORDER BY duration_min DESC\`.`,
                solution: `SELECT 
  title, 
  duration_min,
  RANK() OVER (ORDER BY duration_min DESC) as duration_rank
FROM content`,
                explanation: `**Ranking Without Losing Rows**

**Business Context:**

Ranking powers critical business features:
- "Top 10 most-watched shows" for homepage carousels
- "Your #1 fan in each city" for creator dashboards
- Performance rankings for sales leaderboards

**How Window Functions Work:**

Unlike \`GROUP BY\` which collapses rows, window functions add a calculation column while keeping every row:

| Approach | Rows in Output |
|----------|----------------|
| \`GROUP BY\` | One row per group |
| \`RANK() OVER\` | All original rows |

**Ranking Functions:**

| Function | Ties Behavior | Example: (100, 100, 80) |
|----------|---------------|-------------------------|
| \`RANK()\` | Skips numbers | 1, 1, 3 |
| \`DENSE_RANK()\` | No gaps | 1, 1, 2 |
| \`ROW_NUMBER()\` | No ties | 1, 2, 3 |

**Pro Tip:** Use \`RANK()\` for leaderboards where ties matter; use \`ROW_NUMBER()\` when you need exactly N items regardless of ties.`,
                concepts: [
                        {
                                icon: <FaListOl className="text-3xl text-indigo-400" />,
                                title: 'Window Functions',
                                description: 'Performs calculations across a set of table rows that are somehow related to the current row.'
                        }
                ]
        },
        {
                id: 'partitioned-windows',
                title: 'PARTITION BY - Grouped Ranking',
                question: `Rank content by \`duration_min\` (longest first), but **reset the rank** for each \`genre\`.
Show \`genre\`, \`title\`, \`duration_min\`, and the rank.`,
                keyConcepts: ['PARTITION BY', 'Advanced Ranking', 'Segments'],
                defaultQuery: `-- TIP: PARTITION BY resets the rank for each group
-- RANK() OVER (PARTITION BY category ORDER BY value DESC)`,
                expectedColumns: [
                        { name: 'genre', type: 'TEXT' },
                        { name: 'title', type: 'TEXT' },
                        { name: 'duration_min', type: 'INTEGER' },
                        { name: 'genre_rank', type: 'INTEGER' },
                ],
                businessRequest: `The previous ranking was dominated by long movies in one genre. The team now needs the longest content ranked independently within each genre - so Action, Comedy, and Drama each get their own top-ranked titles.`,
                concept: `**PARTITION BY - Grouped Ranking**

\`PARTITION BY\` inside an \`OVER\` clause splits the window function into independent segments, resetting the calculation for each group. Think of it as running a separate ranking for each category.

\`\`\`sql
RANK() OVER (PARTITION BY category ORDER BY value DESC)
\`\`\`

- \`PARTITION BY\` resets the rank counter for each distinct category value.
- Each partition gets its own sequence starting from 1.
- All rows are still returned - the rank number just restarts per group.

*The common pattern \`WHERE rank <= 3\` after this gives you "Top 3 per group" - used everywhere from Spotify playlists to e-commerce category pages.*`,
                context: `**The Business Request**

The previous list was dominated by Movies.
We need to find the longest content **within each genre** (e.g., the Longest Comedy, the Longest Drama) to treat each category fairly.

**The Concept**

\`PARTITION BY\` splits the ranking logic into separate teams (partitions).
It **resets** the rank counter for each group, so each genre gets its own #1, #2, #3.

**The Syntax**

\`\`\`sql
RANK() OVER (PARTITION BY category ORDER BY value DESC)
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Rank the content by \`duration_min\` (longest first), but **reset the rank** for each \`genre\`.
Show \`genre\`, \`title\`, \`duration_min\`, and the rank.

</div>`,
                hint: `Add \`PARTITION BY genre\` inside your OVER clause, before the ORDER BY.
This tells SQL to restart the ranking for every new genre.`,
                solution: `SELECT 
  genre,
  title,
  duration_min,
  RANK() OVER (PARTITION BY genre ORDER BY duration_min DESC) as genre_rank
FROM content`,
                explanation: `**Segmented Analysis**

**Business Context:**

"Top 3 per category" is everywhere:
- Top songs per genre on Spotify
- Best-selling items per department
- Highest-rated movies per streaming platform

Without \`PARTITION BY\`, you'd need separate queries for each category.

**How PARTITION BY Works:**

\`PARTITION BY\` resets the window function for each group:
- Like \`GROUP BY\` splits data for aggregation
- But keeps all rows in the output
- Think of it as "independent mini-rankings within each bucket"

**The Pattern:**

\`\`\`sql
RANK() OVER (PARTITION BY category ORDER BY value DESC)
\`\`\`

This creates separate rank sequences: Action movies ranked against Action, Comedy against Comedy, etc.

**Common Use Cases:**
- Top N per group: \`WHERE rank <= 3\`
- Latest record per user: \`WHERE row_num = 1\`
- Running totals per account`,
                concepts: [
                        {
                                icon: <FaLayerGroup className="text-3xl text-yellow-500" />,
                                title: 'PARTITION BY',
                                description: 'Divides the result set into partitions to perform window functions on each subset independently.'
                        }
                ]
        },
        {
                id: 'date-functions',
                title: 'Date Functions - Convert & Calculate',
                question: `1. Find sessions where \`watch_start_ts\` is within the last 30 days.
2. Return \`user_id\`, \`watch_date\` (date only), \`signup_date\`, and \`days_since_signup\`.`,
                keyConcepts: ['DATE()', 'DATE_DIFF', 'DATE_ADD', 'Timestamp Conversion'],
                defaultQuery: `-- TIP: DATE() strips the time from a timestamp
-- DATEDIFF('day', start_date, end_date)  ->  days between two dates
-- CURRENT_DATE - INTERVAL '30 days'  ->  relative date filter`,
                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'watch_date', type: 'DATE' },
                        { name: 'signup_date', type: 'DATE' },
                        { name: 'days_since_signup', type: 'INTEGER' },
                ],
                businessRequest: `The Analytics Team needs all sessions from the last 30 days alongside a days-since-signup metric, to understand how quickly new users go from signing up to watching their first content.`,
                concept: `**Date Functions**

Date functions let you extract, compare, and calculate with date and timestamp values. \`DATE()\` strips the time portion from a timestamp. \`DATEDIFF\` measures the gap between two dates.

\`\`\`sql
SELECT DATE(timestamp_col) AS date_only,
       DATEDIFF('day', start_date, end_date) AS days_between
FROM table
WHERE DATE(ts_col) >= CURRENT_DATE - INTERVAL '30 days'
\`\`\`

- \`DATE()\` extracts only the date, discarding hours and minutes.
- \`CURRENT_DATE\` gives today's date without a time component.
- \`DATEDIFF\` returns a signed integer - negative if end is before start.

*Use \`INTERVAL\` for relative filters so the query stays current without manual date updates.*`,
                context: `**The Business Request**

The Analytics Team needs to analyze recent user activity. Find all sessions from the last 30 days and calculate how many days after signup each user watched their first content.

**The Concept**

Date functions help convert and calculate with dates:
- \`DATE()\` extracts just the date from a timestamp
- \`CURRENT_DATE\` gives today's date
- \`DATEDIFF('day', start, end)\` calculates days between two dates
- \`DATE_ADD()\` / \`+ INTERVAL\` to add time periods

**The Syntax**

\`\`\`sql
SELECT 
  DATE(timestamp_col) AS date_only,
  DATEDIFF('day', date1, date2) AS days_between
FROM table
JOIN ... ON ...
WHERE DATE(...) >= CURRENT_DATE - INTERVAL '...' 
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Find sessions where \`watch_start_ts\` is within the last 30 days.
2. Return \`user_id\`, \`watch_date\` (date only), \`signup_date\`, and \`days_since_signup\`.

</div>`,
                hint: `Use \`DATE(watch_start_ts)\` to convert timestamp to date.
Filter with \`WHERE DATE(watch_start_ts) >= CURRENT_DATE - INTERVAL '30 days'\`.
Use \`DATEDIFF('day', signup_date, watch_date)\` to calculate days between dates.`,
                solution: `SELECT 
  vs.user_id,
  DATE(vs.watch_start_ts) AS watch_date,
  DATE(u.signup_ts) AS signup_date,
  DATEDIFF('day', DATE(u.signup_ts), DATE(vs.watch_start_ts)) AS days_since_signup
FROM viewing_sessions vs
JOIN users u ON vs.user_id = u.user_id
WHERE DATE(vs.watch_start_ts) >= CURRENT_DATE - INTERVAL '30 days'
ORDER BY days_since_signup`,
                explanation: `**Date Intelligence Unlocked**

**Business Context:**

Understanding time-to-action is critical for product teams. "Days since signup" reveals:
- **Activation speed**: Do users engage immediately or take time to explore?
- **Onboarding effectiveness**: Long gaps may indicate friction in the signup flow.
- **Cohort behavior**: Compare new user engagement vs. mature users.

**How DATE() Works:**

\`DATE()\` extracts only the date portion from a timestamp, discarding the time component. This is essential when you want to group or compare by calendar day rather than exact moment.

**DATEDIFF Options:**

The first argument controls the unit of measurement:
- \`DATEDIFF('day', start, end)\` -> days between dates
- \`DATEDIFF('week', start, end)\` -> weeks between
- \`DATEDIFF('month', start, end)\` -> months between
- \`DATEDIFF('year', start, end)\` -> years between
- \`DATEDIFF('hour', start, end)\` -> hours between timestamps

**Pro Tip:** DATEDIFF returns a signed integer. If \`end < start\`, you get a negative number.`,
                concepts: [
                        {
                                icon: <FaCalendarAlt className="text-3xl text-green-500" />,
                                title: 'Date Functions',
                                description: 'DATE() converts timestamps to dates. DATEDIFF calculates intervals in days, weeks, months, or years.'
                        }
                ]
        },
        {
                id: 'date-analysis',
                title: 'Date Analysis - Truncation',
                question: `1. Select \`DATE_TRUNC('week', watch_start_ts)\`, \`genre\`, and \`SUM(watch_minutes)\`.
2. Filter for sessions on or after '2023-12-01' AND before '2024-02-01'.
3. Group by the truncated week and genre.
4. Order by the week and genre.`,
                keyConcepts: ['DATE_TRUNC', 'Weekly Aggregation', 'Date Filtering'],
                defaultQuery: `-- TIP: DATE_TRUNC('week', ts) rounds down to the Monday of that week
-- GROUP BY 1  is shorthand for GROUP BY the first SELECT column
-- Use >= start AND < end for clean date range filters`,
                expectedColumns: [
                        { name: 'week_start', type: 'DATE' },
                        { name: 'genre', type: 'TEXT' },
                        { name: 'total_minutes', type: 'INTEGER' },
                ],
                businessRequest: `The Content Team wants to see weekly genre trends during the holiday period - December 2023 through January 2024 - to identify which genres spiked when users had more free time.`,
                concept: `**Date Truncation**

\`DATE_TRUNC\` rounds a timestamp down to the start of a specified time unit. Combined with \`GROUP BY\`, it buckets events into clean weekly or monthly periods for trend analysis.

\`\`\`sql
SELECT DATE_TRUNC('week', ts_col) AS week_start,
       SUM(value)
FROM table
WHERE ts_col >= '2023-12-01'
  AND ts_col < '2024-02-01'
GROUP BY 1
ORDER BY 1
\`\`\`

- \`DATE_TRUNC('week', ts)\` returns the Monday of the week containing that timestamp.
- Use inclusive start and exclusive end for date ranges to avoid 23:59:59 edge cases.
- \`GROUP BY 1\` is shorthand for grouping by the first column in \`SELECT\`.

*Other truncation levels: \`'day'\`, \`'month'\`, \`'quarter'\`, \`'year'\`.*`,
                context: `**The Business Request**

The Content Team wants to see **weekly trends** for our genres.
They specifically want to analyze the holiday season (Dec '23) and the new year (Jan '24) to see which genres spiked when people were off work.

**The Concept**

1. **Truncation**: Raw timestamps (like \`2023-12-25 14:30:00\`) are too granular. To group data by week, we need to **truncate** the timestamp.
\`DATE_TRUNC('week', date_col)\` converts any date into the Monday of that week.

2. **Date Filtering**: To focus on the holiday period, we filter for a specific **Date Range**.
We use an inclusive start and exclusive end pattern:
\`watch_start_ts >= '2023-12-01' AND watch_start_ts < '2024-02-01'\`
This efficiently captures all of December and January.

**The Syntax**

\`\`\`sql
SELECT 
  DATE_TRUNC('week', timestamp_col) as week_start,
  SUM(value)
FROM table
WHERE date_col >= '2023-01-01'
  AND date_col < '2023-02-01'
GROUP BY 1 -- Group by the first column (the week)
ORDER BY 1
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1.  Select \`DATE_TRUNC('week', watch_start_ts)\`, \`genre\`, and \`SUM(watch_minutes)\`.
2.  Filter for sessions **on or after** '2023-12-01' AND **before** '2024-02-01'.
3.  Group By the truncated week and genre.
4.  Order By the week and genre.

</div>`,
                hint: `1. Use \`DATE_TRUNC('week', watch_start_ts)\` to get the Monday of each week.
2. Join \`viewing_sessions\` with \`content\`.
3. Filter \`watch_start_ts\` >= '2023-12-01' AND < '2024-02-01'.`,
                solution: `SELECT 
  DATE_TRUNC('week', watch_start_ts) as week_start,
  content.genre,
  SUM(viewing_sessions.watch_minutes) as total_minutes
FROM viewing_sessions
JOIN content ON viewing_sessions.content_id = content.content_id
WHERE watch_start_ts >= '2023-12-01' 
  AND watch_start_ts < '2024-02-01'
GROUP BY 1, 2
ORDER BY 1, 2`,
                explanation: `**Time Series Created**

**Business Context:**

Weekly views power the dashboards executives check every Monday:
- "Which genre is trending up vs. down?"
- "Did our holiday marketing campaign work?"
- "When do users binge most?"

**How DATE_TRUNC Works:**

\`DATE_TRUNC\` rounds timestamps down to a specified precision:

| Expression | Input | Output |
|------------|-------|--------|
| \`DATE_TRUNC('day', ts)\` | 2023-12-25 14:30:00 | 2023-12-25 00:00:00 |
| \`DATE_TRUNC('week', ts)\` | 2023-12-25 14:30:00 | 2023-12-25 (Monday) |
| \`DATE_TRUNC('month', ts)\` | 2023-12-25 14:30:00 | 2023-12-01 |
| \`DATE_TRUNC('year', ts)\` | 2023-12-25 14:30:00 | 2023-01-01 |

**Date Range Filtering Pattern:**

Use inclusive start, exclusive end:
\`WHERE ts >= '2023-12-01' AND ts < '2024-02-01'\`

This cleanly captures exact months without worrying about 23:59:59 edge cases.

**Pro Tip:** Always \`GROUP BY\` the same expression you \`SELECT\`. Using \`GROUP BY 1\` (column position) is a shortcut.`,
                concepts: [
                        {
                                icon: <FaCalendarAlt className="text-3xl text-red-400" />,
                                title: 'Date Truncation',
                                description: 'Rounding precise timestamps down to a significant unit (day, week, month) for aggregation.'
                        }
                ]
        },
        {
                id: 'cte-architecture',
                title: 'CTEs - Clean Architecture',
                question: `1. Create a CTE called \`UserWatchTime\` that calculates total \`watch_minutes\` per \`user_id\`.
2. Select all columns from this CTE.
3. Filter for users where \`total_watch_time\` is greater than the **average** of all user totals.`,
                keyConcepts: ['WITH', 'CTE', 'Aggregation of Aggregation'],
                defaultQuery: `-- TIP: WITH defines a named temporary result you can query from
-- WITH CTE_Name AS (
--   SELECT ...
-- )
-- SELECT * FROM CTE_Name WHERE ...`,
                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'total_watch_time', type: 'INTEGER' },
                ],
                businessRequest: `The Product Team wants Power Users identified dynamically - anyone watching more than the average user - with the threshold recalculating automatically as the platform grows. No hardcoded numbers.`,
                concept: `**CTEs - Common Table Expressions**

A CTE (defined with \`WITH\`) creates a named temporary result set you can query from - like a readable intermediate step. They are essential when you need two layers of aggregation, since you cannot nest \`SUM\` inside \`AVG\` directly.

\`\`\`sql
WITH Totals AS (
  SELECT user_id, SUM(value) AS total
  FROM source_table
  GROUP BY user_id
)
SELECT *
FROM Totals
WHERE total > (SELECT AVG(total) FROM Totals)
\`\`\`

- Define one or more CTEs before your main \`SELECT\` using \`WITH name AS (...)\`.
- Reference the CTE by name just like a regular table.
- Multiple CTEs can be chained - each can reference the ones defined before it.

*Name CTEs descriptively (\`UserWatchTime\`, not \`temp1\`) - future you will be grateful.*`,
                context: `**The Business Request**

The Product Team wants to identify "Power Users" dynamically.
Instead of a hardcoded threshold (like > 100 mins), they want to find users who watch **more than the average user**.

**The Concept**

This requires a two-step calculation:
1. Calculate the total watch time for **every** user (Aggregation).
2. Calculate the average of **those totals** (Aggregation of an Aggregation).

You cannot nest aggregations like \`AVG(SUM(...))\` directly. You must use a **CTE** to calculate the first layer, then query that CTE to calculate the second layer.

**The Syntax**

\`\`\`sql
WITH UserTotals AS (
  SELECT user_id, SUM(value) as total
  FROM source_table
  GROUP BY user_id
)
SELECT * 
FROM UserTotals 
WHERE total > (SELECT AVG(total) FROM UserTotals)
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Create a CTE called \`UserWatchTime\` that calculates total \`watch_minutes\` per \`user_id\`.
2. Select all columns from this CTE.
3. Filter for users where their \`total_time\` is greater than the **average** of all user totals (using a subquery on the CTE).

</div>`,
                hint: `**Step 1:** Define \`WITH UserWatchTime AS (...)\` to SUM minutes by user. 
**Step 2:** In the main query, select from \`UserWatchTime\`.
**Step 3:** In the WHERE clause, compare \`total_watch_time\` > \`(SELECT AVG(total_watch_time) FROM UserWatchTime)\`.`,
                solution: `WITH UserWatchTime AS (
  SELECT user_id, SUM(watch_minutes) as total_watch_time
  FROM viewing_sessions
  GROUP BY user_id
)
SELECT * 
FROM UserWatchTime
WHERE total_watch_time > (SELECT AVG(total_watch_time) FROM UserWatchTime)`,
                explanation: `**Modular Query Design**

**Business Context:**

Identifying "above average" users dynamically is powerful:
- Power users for beta testing new features
- VIPs for exclusive offers
- Benchmarking individual performance vs. cohort

**How CTEs Work:**

\`WITH\` creates a named temporary result set that exists only for the duration of the query:
1. Define the CTE with \`WITH name AS (SELECT...)\`
2. Reference it by name in subsequent queries
3. Can define multiple CTEs, referencing earlier ones

**CTE vs. Subquery:**

| Feature | CTE | Subquery |
|---------|-----|----------|
| Readability | Named, clear structure | Inline, nested |
| Reusability | Can reference multiple times | Must repeat |
| Self-reference | Supports recursion | No recursion |

**The "Aggregation of Aggregation" Pattern:**

You can't write \`AVG(SUM(...))\`. Instead:
1. CTE calculates \`SUM()\` per group
2. Main query calculates \`AVG()\` of those sums

**Pro Tip:** Name CTEs descriptively (\`UserWatchTime\`, not \`temp1\`). Future you will thank present you.`,
                concepts: [
                        {
                                icon: <FaProjectDiagram className="text-3xl text-gray-600" />,
                                title: 'CTE',
                                description: 'Defines a temporary result set that can be referenced multiple times within a single query.'
                        }
                ]
        },
        {
                id: 'union-stacking',
                title: 'UNION - Stacking Data',
                question: `Create a Master Activity Log:
1. Select \`signup_ts\` (alias as \`event_time\`) and text 'Signup' from \`users\`.
2. UNION
3. Select \`watch_start_ts\` (alias as \`event_time\`) and text 'Session' from \`viewing_sessions\`.`,
                keyConcepts: ['UNION', 'Stacking Sets', 'Vertical Combination'],
                defaultQuery: `-- TIP: UNION stacks two queries vertically - columns must match
-- SELECT col AS alias, 'Label A' AS type FROM table_a
-- UNION
-- SELECT col AS alias, 'Label B' AS type FROM table_b`,
                expectedColumns: [
                        { name: 'event_time', type: 'TIMESTAMP' },
                        { name: 'type', type: 'TEXT' },
                ],
                businessRequest: `The Analytics Team needs a single Master Activity Log combining user signups and video sessions into one chronological feed to visualise total platform activity over time.`,
                concept: `**UNION - Stacking Results**

\`UNION\` stacks the results of two \`SELECT\` queries vertically into one list. Both queries must return the same number of columns with compatible types.

\`\`\`sql
SELECT date_col, 'Label A' AS type FROM table_a
UNION
SELECT date_col, 'Label B' AS type FROM table_b
\`\`\`

- Column names come from the first query.
- \`UNION\` removes duplicate rows automatically (slower).
- \`UNION ALL\` keeps all rows including duplicates (faster - prefer this when sources are distinct).

*Always add a type column like \`'Signup'\` or \`'Session'\` so you can identify the source of each row after combining.*`,
                context: `**The Business Request**

The Analytics Team needs a single "Master Activity Log" that combines **User Signups** and **Video Sessions** into one chronological list to visualize total platform activity.

**The Concept**

\`UNION\` stacks two different tables on top of each other.
Think of it as gluing two lists together vertically. They must have the same number of columns.

**The Syntax**

\`\`\`sql
SELECT date_col, 'Event Type A' FROM table_A
UNION
SELECT date_col, 'Event Type B' FROM table_B
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Create a Master Activity Log.
1. Select \`signup_ts\` (alias as \`event_time\`) and text 'Signup' from \`users\`.
2. UNION
3. Select \`watch_start_ts\` (alias as \`event_time\`) and text 'Session' from \`viewing_sessions\`.

</div>`,
                hint: `Write the first query for \`users\`.
Add the word \`UNION\`.
Write the second query for \`viewing_sessions\`.
Ensure both queries have the same column aliases (e.g., \`AS event_time\`).`,
                solution: `SELECT signup_ts as event_time, 'Signup' as type FROM users
UNION
SELECT watch_start_ts as event_time, 'Session' as type FROM viewing_sessions`,
                explanation: `**Data Stream Merged**

**Business Context:**

Unified event streams power:
- Activity feeds ("User signed up... User watched... User upgraded...")
- Funnel analysis (How quickly from signup to first watch?)
- Anomaly detection (Sudden spike in signups vs. sessions?)

**How UNION Works:**

\`UNION\` stacks query results vertically:
- All column counts must match
- Column **types** must be compatible
- Column **names** come from the first query

| Command | Behavior |
|---------|----------|
| \`UNION\` | Removes duplicates (slower) |
| \`UNION ALL\` | Keeps all rows (faster) |

**When to Use Each:**
- \`UNION ALL\`: Different tables with unique IDs (faster, no dedup needed)
- \`UNION\`: Combining overlapping data where deduplication matters

**Pro Tip:** Add a type column (\`'Signup'\`, \`'Session'\`) to identify the source of each row after combining.`,
                concepts: [
                        {
                                icon: <FaLayerGroup className="text-3xl text-pink-500" />,
                                title: 'UNION',
                                description: 'Combines the result sets of two or more queries into a single vertical list.'
                        }
                ]
        },
        {
                id: 'capstone-intermediate',
                title: 'Capstone: The Engagement Report',
                question: `1. Create a CTE \`GenreStats\` - total \`watch_minutes\` per \`country_code\` and \`genre\` for **Premium** users.
2. Create a CTE \`GenreRankings\` - select from \`GenreStats\`, add \`RANK()\` of genres within each country.
3. Select from \`GenreRankings\` where rank is 3 or less.
4. Return \`country_code\`, \`genre\`, \`total_watch_time\`, and \`genre_rank\`.`,
                keyConcepts: ['CTE', 'Window Functions', '4-Table Join', 'Business Logic'],
                defaultQuery: `-- TIP: Chain CTEs - each one builds on the previous
-- WITH CTE_One AS ( ... ),
--      CTE_Two AS ( SELECT *, RANK() OVER (...) FROM CTE_One )
-- SELECT ... FROM CTE_Two WHERE rank <= 3`,
                expectedColumns: [
                        { name: 'country_code', type: 'TEXT' },
                        { name: 'genre', type: 'TEXT' },
                        { name: 'total_watch_time', type: 'INTEGER' },
                        { name: 'genre_rank', type: 'INTEGER' },
                ],
                businessRequest: `The Executive Team needs the top 3 most-watched genres per country for premium subscribers only. This analysis will directly inform which content to license for each market in the upcoming quarter.`,
                concept: `**Chained CTEs**

Complex multi-step analysis is best structured as chained CTEs. Each CTE handles one concern cleanly - making the query readable, debuggable, and easy to modify.

\`\`\`sql
WITH Aggregated AS (
  SELECT ..., SUM(...) AS total
  FROM ... JOIN ...
  WHERE condition
  GROUP BY ...
),
Ranked AS (
  SELECT *, RANK() OVER (PARTITION BY group_col ORDER BY total DESC) AS rnk
  FROM Aggregated
)
SELECT * FROM Ranked WHERE rnk <= 3
\`\`\`

- CTE 1 handles data assembly and aggregation.
- CTE 2 applies the window function to rank within groups.
- The final \`SELECT\` filters to the top N.

*Break each step into a named CTE that does one thing. The query becomes self-documenting.*`,
                context: `**The Business Request**

The Executive Team demands a **Strategic Content Analysis**.
They need to identify the **Top 3 most watched genres** for each country, specifically for our **Premium** subscribers. This data will directly inform the content acquisition budget for the upcoming quarter.

**The Architecture**

This analysis requires a multi-step architectural approach:
1.  **Traverse the Schema:** Connect \`viewing_sessions\` -> \`users\` -> \`subscriptions\` -> \`content\`.
2.  **Apply Business Logic:** Filter for only \`premium\` plans.
3.  **Aggregate:** Sum total watch time per \`country\` and \`genre\`.
4.  **Rank:**  Rank genres within each country by popularity.
5.  **Filter:** Select the top 3 ranks.

**The Syntax Skeleton**

\`\`\`sql
WITH GenreStats AS (
    -- Step 1: Join tables & Aggregate Totals
    SELECT country, genre, SUM(minutes)
    FROM ...
    GROUP BY 1, 2
),
GenreRankings AS (
    -- Step 2: Apply Window Function
    SELECT *, RANK() OVER (PARTITION BY ... ORDER BY ...)
    FROM GenreStats
)
-- Step 3: Final Filtering
SELECT * 
FROM GenreRankings 
WHERE rank <= 3
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1.  Create a CTE \`GenreStats\` to calculate total \`watch_minutes\` per \`country_code\` and \`genre\` for **Premium** users.
2.  Create a second CTE \`GenreRankings\` that selects from \`GenreStats\` and adds a \`RANK()\` of genres within each country.
3.  Finally, select from \`GenreRankings\` where the rank is **3 or less**.
4.  Return \`country_code\`, \`genre\`, \`total_watch_time\`, and \`genre_rank\`.

</div>`,
                hint: `**Architecture Guide:**
1. \`GenreStats\`: Join all 4 tables. Filter \`subscriptions.plan = 'premium'\`. Group by country/genre.
2. \`GenreRankings\`: Select from \`GenreStats\`. Add \`RANK() OVER (PARTITION BY country_code ORDER BY total_watch_time DESC)\`.
3. Final Query: \`SELECT * FROM GenreRankings WHERE genre_rank <= 3\`.`,
                solution: `WITH GenreStats AS (
  SELECT 
    users.country_code, 
    content.genre, 
    SUM(viewing_sessions.watch_minutes) as total_watch_time
  FROM viewing_sessions
  JOIN users ON viewing_sessions.user_id = users.user_id
  JOIN content ON viewing_sessions.content_id = content.content_id
  JOIN subscriptions ON users.user_id = subscriptions.user_id
  WHERE subscriptions.plan = 'premium'
  GROUP BY 1, 2
),
GenreRankings AS (
  SELECT 
    *,
    RANK() OVER (PARTITION BY country_code ORDER BY total_watch_time DESC) as genre_rank
  FROM GenreStats
)
SELECT * 
FROM GenreRankings
WHERE genre_rank <= 3`,
                explanation: `**Real-World Analytics Delivered**

**Business Context:**

This query answers a question executives actually ask:
"What content should we invest in for each market?"

The answer directly influences:
- Content licensing budgets by region
- Localization priorities
- Marketing spend allocation

**Skills Combined:**

You synthesized everything from this case:
- **CTEs**: Modular, readable architecture
- **Multi-Table Joins**: Connecting users, subscriptions, sessions, content
- **Aggregations**: SUM for total engagement
- **Window Functions**: RANK with PARTITION BY for "top N per group"
- **Filtering**: Premium users only, top 3 per country

**Production-Ready Pattern:**

This "Top N per Group" pattern appears everywhere:
- Top 3 products per category on e-commerce
- Top 5 songs per genre on streaming
- Top 10 videos per channel on YouTube

**Architecture Best Practice:** Break complex problems into named steps (Aggregation -> Ranking -> Filtering). Each CTE does ONE thing clearly.`,
                concepts: [
                        {
                                icon: <FaTrophy className="text-3xl text-yellow-500" />,
                                title: 'Mastery',
                                description: 'Demonstrates the ability to synthesize complex SQL concepts into efficient business solutions.'
                        }
                ]
        }
];
